import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { motion } from 'motion/react'
import { Gift, Heart, HeartCrack, PiggyBank, Lock, LogOut } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useLanguage } from '@/context/LanguageContext'
import { useAuth } from '@/hooks/useAuth'
import { useSEO } from '@/hooks/useSEO'
import { usePrerenderReady } from '@/hooks/usePrerenderReady'
import type { Profile } from '@/hooks/useAuth'
import { findBoardByParam, orderCategories } from '@/lib/wishlist'
import FavoriteBoardButton from '@/components/wishlist/FavoriteBoardButton'
import { setCustomCategoryColors } from '@/lib/categoryColors'
import type { Wishlist, WishlistCategory, WishlistItem } from '@/lib/wishlist'
import ItemCard from '@/components/wishlist/ItemCard'
import { getAppreciationVisitorToken } from '@/components/wishlist/ItemAppreciationButton'
import ItemDetailDialog from '@/components/wishlist/ItemDetailDialog'
import CategoryChips from '@/components/wishlist/CategoryChips'
import DonationDialog from '@/components/wishlist/DonationDialog'

type LoadState = 'loading' | 'ready' | 'not-found' | 'private'

export default function PublicWishlistPage() {
  const { username = '', boardSlug: routeBoardSlug } = useParams<{ username: string; boardSlug?: string }>()
  const [searchParams] = useSearchParams()
  const boardParam = routeBoardSlug ?? searchParams.get('board')
  const { user, signOut } = useAuth()
  const { language } = useLanguage()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [items, setItems] = useState<WishlistItem[]>([])
  const [appreciations, setAppreciations] = useState<Record<string, { count: number; liked: boolean }>>({})
  const [state, setState] = useState<LoadState>('loading')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [customCategories, setCustomCategories] = useState<WishlistCategory[]>([])
  const [boards, setBoards] = useState<Wishlist[]>([])
  const [singleBoardMode, setSingleBoardMode] = useState(false)
  const [activeBoardId, setActiveBoardId] = useState<string | null>(null)
  const [selectedItem, setSelectedItem] = useState<WishlistItem | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [donationOpen, setDonationOpen] = useState(false)
  const [privateCoverUrl, setPrivateCoverUrl] = useState<string | null>(null)
  const [boardFavorites, setBoardFavorites] = useState<Record<string, { count: number; favorited: boolean }>>({})

  const handleItemReserved = (updated: { id: string; is_reserved: boolean; reserved_by: string | null; reserved_session_id: string | null; purchase_count?: number }) => {
    setItems((current) => current.map((entry) => entry.id === updated.id ? { ...entry, ...updated } as WishlistItem : entry))
    setSelectedItem((current) => current?.id === updated.id ? { ...current, ...updated } as WishlistItem : current)
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      setState('loading')
      setAppreciations({})
      const uname = username.toLowerCase()
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uname)
      const { data: profileRows, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .ilike('username', uname)
      if (cancelled) return

      if ((!profileRows || profileRows.length === 0) && isUuid) {
        const { data: byId } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', uname)
        if (cancelled) return
        profileRows.push(...(byId ?? []))
      }

      if (cancelled) return
      const found = (profileRows?.[0] ?? null) as unknown as Profile | null
      if (profileError || !found) {
        setState('not-found')
        return
      }
      setProfile(found)

      const { data: boardsData } = await supabase
        .from('wishlists')
        .select('*')
        .eq('user_id', found.id)
        .order('created_at', { ascending: true })

      if (cancelled) return
      const userBoards = (boardsData ?? []) as unknown as Wishlist[]

      // If no boards are visible, the wishlist is private (RLS hides private boards from non-owners)
      const isOwnerOfProfile = user?.id === found.id
      if (!isOwnerOfProfile && userBoards.length === 0) {
        setState('private')
        // Still register a page view
        supabase.from('page_views').insert({
          profile_id: found.id,
          path: `/w/${username}`,
        }).then(() => {})
        return
      }

      const sharedBoard = findBoardByParam(userBoards, boardParam)
      const isOwnerOfBoards = user?.id === found.id
      if (!isOwnerOfBoards) {
        const publicBoardIds = userBoards.filter((board) => !board.is_private).map((board) => board.id)
        if (publicBoardIds.length > 0) {
          try {
            const { data: favRows, error: favError } = await supabase.rpc('get_board_favorites', {
              _wishlist_ids: publicBoardIds,
            })
            if (cancelled) return
            if (favError) throw favError
            const nextFavorites: Record<string, { count: number; favorited: boolean }> = {}
            for (const row of (favRows as unknown as { wishlist_id: string; favorite_count: number; favorited: boolean }[]) ?? []) {
              nextFavorites[row.wishlist_id] = { count: row.favorite_count, favorited: row.favorited }
            }
            setBoardFavorites(nextFavorites)
          } catch (error) {
            console.error('Unable to load board favorites', error)
          }
        }
      }
      setSingleBoardMode(!!sharedBoard)
      setBoards(userBoards)
      setActiveBoardId(sharedBoard ? sharedBoard.id : (userBoards.length > 0 ? userBoards[0].id : null))
      const initialBoardId = sharedBoard ? sharedBoard.id : (userBoards.length > 0 ? userBoards[0].id : null)

      const { data: itemsData } = await supabase
        .from('wishlist_items')
        .select('*')
        .eq('user_id', found.id)
        .eq('is_hidden', false)
        .order('position', { ascending: true })
        .order('created_at', { ascending: false })

      if (cancelled) return
      const loadedItems = (itemsData as unknown as WishlistItem[]) ?? []
      setItems(loadedItems)
      const publicGuideIds = new Set(userBoards.filter((board) => board.kind === 'guide' && !board.is_private).map((board) => board.id))
      const guideItemIds = loadedItems.filter((item) => item.wishlist_id && publicGuideIds.has(item.wishlist_id)).map((item) => item.id)
      if (guideItemIds.length > 0) {
        try {
          const visitorToken = getAppreciationVisitorToken()
          const nextAppreciations: Record<string, { count: number; liked: boolean }> = {}
          for (let offset = 0; offset < guideItemIds.length; offset += 500) {
            const { data, error } = await supabase.rpc('get_guide_item_appreciations', {
              _item_ids: guideItemIds.slice(offset, offset + 500),
              _visitor_token: visitorToken,
            })
            if (cancelled) return
            if (error) throw error
            const rows = data as unknown as { item_id: string; appreciation_count: number; liked: boolean }[] | null
            for (const row of rows ?? []) {
              nextAppreciations[row.item_id] = { count: row.appreciation_count, liked: row.liked }
            }
          }
          if (!cancelled) setAppreciations(nextAppreciations)
        } catch (error) {
          console.error('Unable to load guide appreciations', error)
        }
      }
      if (cancelled) return

      const { data: catsData } = await supabase
        .from('wishlist_categories')
        .select('*')
        .eq('user_id', found.id)
        .order('position', { ascending: true })

      if (cancelled) return
      const cats = (catsData ?? []) as unknown as WishlistCategory[]
      setCustomCategories(cats)
      setCustomCategoryColors(found.id, cats)
      setState('ready')

      // Register a page view (fire-and-forget, no error handling needed)
      supabase.from('page_views').insert({
        profile_id: found.id,
        path: `/w/${username}`,
        wishlist_id: initialBoardId,
      }).then(() => {})
    }
    load()
    return () => {
      cancelled = true
    }
  }, [username, boardParam])

  const isOwner = user?.id === profile?.id
  const boardFiltered = useMemo(
    () =>
      boards.length > 0
        ? items.filter((i) => (i.wishlist_id ?? null) === activeBoardId)
        : items,
    [items, boards, activeBoardId]
  )
  const boardCategories = useMemo(
    () =>
      orderCategories(
        Array.from(new Set(boardFiltered.map((i) => i.category))),
        customCategories
      ),
    [boardFiltered, customCategories]
  )
  const boardCounts = useMemo(() => {
    const map: Record<string, number> = {}
    for (const i of boardFiltered) map[i.category] = (map[i.category] ?? 0) + 1
    return map
  }, [boardFiltered])
  const filtered = useMemo(
    () =>
      activeCategory
        ? boardFiltered.filter((i) => i.category === activeCategory)
        : boardFiltered,
    [boardFiltered, activeCategory]
  )

  const activeBoard = useMemo(
    () => boards.find((b) => b.id === activeBoardId) ?? null,
    [boards, activeBoardId]
  )
  const isGuide = activeBoard?.kind === 'guide'

  const displayName = profile?.display_name || (profile?.username ? `@${profile.username}` : 'Anonimo')
  const initials = displayName.replace('@', '').slice(0, 2).toUpperCase()

  useSEO({
    title: profile ? `Make me a Gift - ${displayName}` : 'Make me a Gift',
    description: 'Le sorprese sono belle... ma se poi non ti piace?! Crea la tua lista dei desideri online con Make me a Gift!',
    ogImage: '/images/opengraph.jpg',
    canonicalUrl: typeof window !== 'undefined' ? window.location.href : undefined,
  })
  usePrerenderReady()

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 glass-strong border-b border-border/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-extrabold">
            <span className="w-8 h-8 rounded-lg bg-neutral-900 text-white flex items-center justify-center">
              <Gift className="w-4 h-4" />
            </span>
            <span className="text-base font-semibold sm:text-lg font-logo">Make me a Gift</span>
          </Link>
          {user ? (
            <div className="flex items-center gap-2">
              <Link
                to="/dashboard"
                className="text-xs sm:text-sm font-medium text-white bg-neutral-900 rounded-full px-4 sm:px-5 py-2 sm:py-2.5 hover:bg-neutral-800 transition-colors"
              >
                Dashboard
              </Link>
              <button
                onClick={() => signOut()}
                className="w-9 h-9 flex items-center justify-center rounded-full text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                title="Esci"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Link
              to="/register"
              className="text-xs sm:text-sm font-medium text-white bg-neutral-900 rounded-full px-4 sm:px-5 py-2 sm:py-2.5 hover:bg-neutral-800 transition-colors"
            >
              <span className="hidden md:inline">{language === 'en' ? 'Create your wishlist too' : 'Crea anche tu la tua wishlist'}</span>
              <span className="md:hidden">{language === 'en' ? 'Create Wishlist' : 'Crea Wishlist'}</span>
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 pb-12 sm:py-12">
        {state === 'loading' && (
          <div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-muted animate-pulse" />
              <div className="space-y-2">
                <div className="h-6 w-48 bg-muted rounded-full animate-pulse" />
                <div className="h-4 w-28 bg-muted rounded-full animate-pulse" />
              </div>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
              {[1, 2, 3].map((n) => (
                <div key={n} className="glass rounded-2xl overflow-hidden">
                  <div className="aspect-[4/3] bg-muted animate-pulse" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 w-3/4 bg-muted rounded-full animate-pulse" />
                    <div className="h-3 w-1/3 bg-muted rounded-full animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {state === 'not-found' && (
          <div className="py-24 text-center">
            <div className="mx-auto w-20 h-20 rounded-full bg-neutral-200 flex items-center justify-center">
              <HeartCrack className="w-10 h-10 text-neutral-900" />
            </div>
            <h1 className="mt-6 text-3xl font-extrabold tracking-tight">Wishlist non trovata</h1>
            <p className="mt-3 text-muted-foreground">Questo link non corrisponde a nessuna lista. Controlla l'indirizzo.</p>
            <Link
              to="/"
              className="inline-block mt-8 text-sm font-medium text-white bg-neutral-900 rounded-full px-5 py-2.5 hover:bg-neutral-800 transition-colors"
            >
              Torna alla home
            </Link>
          </div>
        )}

        {state === 'private' && profile && (
          <>
            {/* Cover image if available */}
            {(profile as any)?.wishlist_cover_url ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative rounded-xl overflow-hidden border border-border min-h-[200px] sm:min-h-[260px] flex flex-col justify-end"
              >
                <img
                  src={(profile as any).wishlist_cover_url} style={{ objectPosition: `50% ${(profile as any).wishlist_cover_position ?? 50}%` }}
                  alt="Cover"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="relative z-10 p-6 sm:p-8">
                  <div className="flex items-center gap-4">
                    {profile.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt={displayName}
                        className="w-14 h-14 rounded-full object-cover ring-4 ring-white/90 shadow-lg shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-neutral-900 text-white font-extrabold text-lg flex items-center justify-center ring-4 ring-white/90 shadow-lg shrink-0">
                        {initials}
                      </div>
                    )}
                    <div>
                      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white drop-shadow">
                        {profile.wishlist_title?.trim() || `La wishlist di ${displayName}`}
                      </h1>
                      <p className="mt-0.5 text-white/80 text-sm font-medium">
                        {profile.username && `@${profile.username}`}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              /* Profile info without cover */
              <motion.div
                className="flex flex-col sm:flex-row sm:items-center gap-5"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={displayName}
                    className="w-16 h-16 rounded-full object-cover ring-4 ring-white shadow"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-neutral-900 text-white font-extrabold text-xl flex items-center justify-center ring-4 ring-white shadow">
                    {initials}
                  </div>
                )}
                <div>
                  <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                    {profile.wishlist_title?.trim() || `La wishlist di ${displayName}`}
                  </h1>
                  {profile.username && (
                    <p className="mt-1 text-muted-foreground text-sm font-medium">
                      @{profile.username}
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Two-column layout: lock notice (1/3) + donation box (2/3) */}
            <div className="mt-8 flex flex-col lg:flex-row gap-6 items-stretch">
              {/* Left column — Lock notice (1/3) */}
              <div className="w-full lg:w-1/3 mx-auto lg:mx-0">
                <div className="relative overflow-hidden rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-8 sm:p-10 h-full flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 rounded-full bg-[#D4F1E4] flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <Lock className="w-8 h-8 text-[#5BA39C]" />
                  </div>
                  <p className="mt-4 text-lg font-semibold text-neutral-700">
                    Questa wishlist &egrave; privata
                  </p>
                </div>
              </div>

              {/* Right column — Donation box (2/3) */}
              {((profile as any)?.donation_enabled && ((profile as any)?.donation_paypal_email || (profile as any)?.donation_link)) ? (
                <div className="w-full lg:w-2/3">
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="relative overflow-hidden rounded-2xl border-2 border-pink-200 bg-gradient-to-br from-pink-50 via-white to-amber-50 p-5 sm:p-8 md:p-10 h-full"
                  >
                    <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                      <div className="shrink-0">
                        <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-pink-500 flex items-center justify-center shadow-lg shadow-pink-500/30">
                          <PiggyBank className="h-7 w-7 sm:h-10 sm:w-10 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 text-center sm:text-left w-full sm:w-auto sm:max-w-[calc(100%-145px)]">
                        <h2 className="text-base sm:text-2xl font-extrabold text-neutral-900 flex items-center gap-2 justify-center sm:justify-start whitespace-nowrap">
                          <Heart className="h-5 w-5 text-pink-500 shrink-0" />
                          <span>{language === 'en' ? 'Support me with a donation' : 'Sostienimi con una donazione'}</span>
                        </h2>
                        <p className="mt-2 text-xs sm:text-sm text-neutral-600 leading-relaxed mx-auto sm:mx-0">
                          {(profile as any)?.donation_message || (language === 'en'
                            ? `Would you rather make a free donation instead of picking a gift? Every contribution is welcome and helps ${displayName} make their wishes come true.`
                            : `Preferisci fare una donazione libera invece di scegliere un regalo? Ogni contributo \u00e8 benvenuto e aiuta ${displayName} a realizzare i suoi desideri.`)}
                        </p>
                      </div>
                      <div className="shrink-0 mt-3 sm:mt-0 sm:mr-2 flex justify-center sm:block">
                        <button
                          onClick={() => setDonationOpen(true)}
                          className="inline-flex items-center justify-center gap-2 rounded-full bg-pink-500 hover:bg-pink-600 px-6 py-3 sm:px-7 sm:py-3.5 text-xs sm:text-sm font-bold text-white transition-colors shadow-md shadow-pink-500/20"
                        >
                          <PiggyBank className="h-4 w-4" />
                          {language === 'en' ? 'Donate now' : 'Dona ora'}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ) : (
                <div className="lg:w-2/3 hidden lg:block" />
              )}
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/"
                className="inline-block text-sm font-medium text-white bg-neutral-900 rounded-full px-5 py-2.5 hover:bg-neutral-800 transition-colors"
              >
                Torna alla home
              </Link>
            </div>
          </>
        )}

        {state === 'ready' && profile && (
          <>
            {activeBoard?.cover_url ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative rounded-xl overflow-hidden border border-border min-h-[200px] sm:min-h-[260px] flex flex-col justify-end"
              >
                <img
                  src={activeBoard.cover_url}
                    style={{ objectPosition: `50% ${activeBoard.cover_position ?? 50}%` }}
                  alt="Cover"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                <div className="relative z-10 p-6 sm:p-8 flex flex-col gap-4">
                  <div className="flex items-center gap-4">
                    {boardFavorites[activeBoard.id] && (
                      <div className="ml-auto shrink-0">
                        <FavoriteBoardButton
                          wishlistId={activeBoard.id}
                          initialCount={boardFavorites[activeBoard.id].count}
                          initiallyFavorited={boardFavorites[activeBoard.id].favorited}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {profile.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt={displayName}
                        className="w-14 h-14 rounded-full object-cover ring-4 ring-white/90 shadow-lg shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-neutral-900 text-white font-extrabold text-lg flex items-center justify-center ring-4 ring-white/90 shadow-lg shrink-0">
                        {initials}
                      </div>
                    )}
                    <div>
                      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white drop-shadow">
                        {profile.wishlist_title?.trim() || (isGuide ? (language === 'en' ? `${displayName}'s advice` : `I consigli di ${displayName}`) : (language === 'en' ? `${displayName}'s wishlist` : `La wishlist di ${displayName}`))}
                      </h1>
                      <p className="mt-0.5 text-white/80 text-sm font-medium">
                        {boardFiltered.length} {boardFiltered.length === 1 ? (language === 'en' ? 'item' : 'articolo') : (language === 'en' ? 'items' : 'articoli')}
                        {profile.username && ` \u00b7 @${profile.username}`}
                      </p>
                      <span className={`mt-2 inline-flex items-center rounded-full px-3 py-0.5 text-xs font-bold ${isGuide ? 'bg-white/90 text-pink-700' : 'bg-white/90 text-neutral-700'}`}>
                        {isGuide ? (language === 'en' ? 'Advice' : 'Consigli') : 'Wishlist'}
                      </span>
                    </div>
                  </div>

                  {boards.length > 1 && !singleBoardMode && (
                    <div className="flex flex-wrap gap-2">
                      {boards.map((b) => (
                        <button
                          key={b.id}
                          onClick={() => { setActiveBoardId(b.id); setActiveCategory(null) }}
                          className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                            b.id === activeBoardId
                              ? 'bg-white text-neutral-900'
                              : 'bg-white/20 text-white backdrop-blur hover:bg-white/30'
                          }`}
                        >
                          {b.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ) : (
              <>
                <motion.div
                  className="flex flex-col sm:flex-row sm:items-center gap-5"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {profile.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={displayName}
                      className="w-16 h-16 rounded-full object-cover ring-4 ring-white shadow"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-neutral-900 text-white font-extrabold text-xl flex items-center justify-center ring-4 ring-white shadow">
                      {initials}
                    </div>
                  )}
                  <div>
                    <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                      {profile.wishlist_title?.trim() || (isGuide ? (language === 'en' ? `${displayName}'s advice` : `I consigli di ${displayName}`) : (language === 'en' ? `${displayName}'s wishlist` : `La wishlist di ${displayName}`))}
                    </h1>
                    <p className="mt-1 text-muted-foreground text-sm font-medium">
                      {boardFiltered.length} {boardFiltered.length === 1 ? (language === 'en' ? 'item' : 'articolo') : (language === 'en' ? 'items' : 'articoli')}
                      {profile.username && ` \u00b7 @${profile.username}`}
                    </p>
                    <span className={`mt-2 inline-flex items-center rounded-full px-3 py-0.5 text-xs font-bold ${isGuide ? 'bg-pink-100 text-pink-700' : 'bg-neutral-100 text-neutral-700'}`}>
                      {isGuide ? (language === 'en' ? 'Advice' : 'Consigli') : 'Wishlist'}
                    </span>
                  </div>
                  {activeBoard && boardFavorites[activeBoard.id] && (
                    <div className="sm:ml-auto">
                      <FavoriteBoardButton
                        wishlistId={activeBoard.id}
                        initialCount={boardFavorites[activeBoard.id].count}
                        initiallyFavorited={boardFavorites[activeBoard.id].favorited}
                      />
                    </div>
                  )}
                </motion.div>

                {boards.length > 1 && !singleBoardMode && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {boards.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => { setActiveBoardId(b.id); setActiveCategory(null) }}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                          b.id === activeBoardId
                            ? 'bg-neutral-900 text-white'
                            : 'glass text-muted-foreground hover:text-neutral-900 hover:border-primary/40'
                        }`}
                      >
                        {b.title}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {boardFiltered.length === 0 ? (
              <div className="py-24 text-center">
                <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <Gift className="w-10 h-10 text-primary" />
                </div>
                <p className="mt-6 text-xl font-bold">Nessun desiderio qui… ancora!</p>
                <p className="mt-2 text-muted-foreground">Torna più tardi, la lista è in arrivo.</p>
              </div>
            ) : (
              <>
                <div className="mt-8">
                  <CategoryChips
                    categories={boardCategories}
                    active={activeCategory}
                    onChange={setActiveCategory}
                    counts={boardCounts}
                  />
                </div>
                <motion.div
                  className="mt-6 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6"
                  initial="hidden"
                  animate="visible"
                  variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
                >
                  {filtered.map((wishlistItem) => (
                    <motion.div
                      key={wishlistItem.id}
                      variants={{
                        hidden: { opacity: 0, y: 24 },
                        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
                      }}
                    >
                      <ItemCard
                        item={wishlistItem}
                        onClick={() => { setSelectedItem(wishlistItem); setDetailOpen(true) }}
                        isOwner={isOwner}
                        isGuide={isGuide}
                        appreciation={appreciations[wishlistItem.id] ?? (isGuide ? { count: 0, liked: false } : undefined)}
                        onAppreciationChange={(next) => setAppreciations((prev) => ({ ...prev, [wishlistItem.id]: next }))}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              </>
            )}

            {/* Donation section — only show if the owner has configured donations */}
            {(!isGuide && (profile as any)?.donation_enabled && ((profile as any)?.donation_paypal_email || (profile as any)?.donation_link)) && (
              <div className="mt-12 w-full max-w-[780px] mx-auto px-4">
                <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="relative overflow-hidden rounded-2xl border-2 border-pink-200 bg-gradient-to-br from-pink-50 via-white to-amber-50 p-5 sm:p-8 md:p-10"
              >
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                  <div className="shrink-0">
                    <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-pink-500 flex items-center justify-center shadow-lg shadow-pink-500/30">
                      <PiggyBank className="h-7 w-7 sm:h-10 sm:w-10 text-white" />
                    </div>
                  </div>
                  <div className="flex-1 text-center sm:text-left w-full sm:w-auto">
                    <h2 className="text-base sm:text-2xl font-extrabold text-neutral-900 flex items-center gap-2 justify-center sm:justify-start whitespace-nowrap">
                      <Heart className="h-5 w-5 text-pink-500 shrink-0" />
                      <span>{language === 'en' ? 'Support me with a donation' : 'Sostienimi con una donazione'}</span>
                    </h2>
                    <p className="mt-2 text-xs sm:text-sm text-neutral-600 leading-relaxed sm:max-w-sm mx-auto sm:mx-0 sm:pr-4">
                      {(profile as any)?.donation_message || (language === 'en'
                        ? `Would you rather make a free donation instead of picking a gift? Every contribution is welcome and helps ${displayName} make their wishes come true.`
                        : `Preferisci fare una donazione libera invece di scegliere un regalo? Ogni contributo \u00e8 benvenuto e aiuta ${displayName} a realizzare i suoi desideri.`)}
                    </p>
                  </div>
                  <div className="shrink-0 mt-3 sm:mt-0 sm:mr-2 flex justify-center sm:block">
                    <button
                      onClick={() => setDonationOpen(true)}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-pink-500 hover:bg-pink-600 px-6 py-3 sm:px-7 sm:py-3.5 text-xs sm:text-sm font-bold text-white transition-colors shadow-md shadow-pink-500/20"
                    >
                      <PiggyBank className="h-4 w-4" />
                      {language === 'en' ? 'Donate now' : 'Dona ora'}
                    </button>
                  </div>
                </div>
              </motion.div>
              </div>
            )}
          </>
        )}
      </main>

      <footer className="mt-12 bg-neutral-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-white text-neutral-900 flex items-center justify-center">
              <Gift className="w-4 h-4" />
            </span>
            <span className="text-lg font-semibold font-logo">Make me a Gift</span>
          </Link>
          <p className="text-sm text-neutral-400">{language === 'en' ? 'Made with ♥ by' : 'Fatto con ♥ da'} <strong className="font-semibold text-neutral-300">Aura Nuccio</strong> {language === 'en' ? 'with' : 'con'} Coderblock.ai</p>
          <div className="flex items-center justify-center gap-3 whitespace-nowrap text-[11px] sm:gap-6 sm:text-sm">
            <Link to="/cookie-policy" className="text-neutral-300 hover:text-white transition-colors">Cookie Policy</Link>
            <Link to="/privacy" className="text-neutral-300 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-neutral-300 hover:text-white transition-colors">{language === 'en' ? 'Terms & Conditions' : 'Termini e Condizioni'}</Link>
          </div>
        </div>
      </footer>

      <ItemDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        item={selectedItem}
        items={filtered}
        onNavigate={setSelectedItem}
        isOwner={isOwner}
          isGuide={isGuide}
          onItemReserved={handleItemReserved}
          ownerDisplayName={displayName}
          ownerUsername={profile?.username ?? null}
          ownerShippingAddress={(profile as any)?.shipping_address ?? null}
          ownerShippingRecipient={(profile as any)?.shipping_recipient ?? null}
      />

      {(profile as any)?.donation_enabled && (
        <DonationDialog
          open={donationOpen}
          onOpenChange={setDonationOpen}
          displayName={displayName}
          paypalEmail={(profile as any)?.donation_paypal_email ?? null}
          paypalLink={(profile as any)?.donation_link ?? null}
          message={(profile as any)?.donation_message ?? null}
        />
      )}
    </div>
  )
}
